import { useEffect, useRef, useState, type ReactNode } from "react";
import { AlertCircle, CheckCircle2, Circle, CircleDashed, CircleOff, Clock, FlaskConical } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { cn } from "./lib/utils";
import type { ConsentStatus, NoticeGroup, NoticeVersion } from "./data";
import { catalogLanguages, formatPurposeRetention, formatRetention, languageCode, languageLabel } from "./catalog-map";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

export function Req({ children, show = true }: { children: ReactNode; show?: boolean }) {
  if (!show) return null;
  return <span className="ml-2 inline-flex rounded border border-border bg-muted px-1.5 py-0.5 align-middle text-[10px] font-semibold text-muted-foreground">{children}</span>;
}

export function Status({ value }: { value: ConsentStatus | string }) {
  const success = value === "Active" || value === "Published" || value === "Fulfilled" || value === "Completed";
  const progress = value === "In progress" || value === "Assigned";
  const pending = value === "Submitted" || value === "Open";
  const danger = value === "Rejected" || value === "Cancelled" || value === "Declined";
  const muted = value === "Withdrawn" || value === "Superseded" || value === "Closed";
  const Icon = success ? CheckCircle2 : progress ? Clock : danger ? AlertCircle : muted ? CircleOff : pending ? CircleDashed : Circle;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
        success && "border-success/25 bg-success-soft text-success",
        progress && "border-info/25 bg-info-soft text-info",
        pending && "border-warning/30 bg-warning-soft text-warning-foreground",
        danger && "border-destructive/25 bg-destructive-soft text-destructive",
        muted && "border-border bg-muted text-muted-foreground",
        !success && !progress && !pending && !danger && !muted && "border-border bg-background text-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {value}
    </span>
  );
}

export function MockDataSwitch({ on, setOn }: { on: boolean; setOn: (v: boolean) => void }) {
  return <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Switch checked={on} onCheckedChange={setOn} aria-label="Mock data" />Mock data</label>;
}

export function AnnotationToggle({ on, setOn }: { on: boolean; setOn: (v: boolean) => void }) {
  return <Button variant={on ? "secondary" : "outline"} size="sm" onClick={() => setOn(!on)}><FlaskConical /> {on ? "Annotations on" : "Show requirement IDs"}</Button>;
}

export function PageTitle({ eyebrow, title, description, req, annotated, actions }: { eyebrow: string; title: string; description: string; req: string; annotated: boolean; actions?: ReactNode }) {
  return <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4 border-b border-border pb-5"><div className="min-w-0"><p className="mb-2 text-xs font-bold uppercase text-primary">{eyebrow}</p><h1 className="text-2xl font-bold text-foreground sm:text-3xl">{title}<Req show={annotated}>{req}</Req></h1><p className="mt-2 max-w-2xl text-sm text-muted-foreground">{description}</p></div>{actions && <div className="shrink-0">{actions}</div>}</div>;
}

export function Field({ label, value }: { label: string; value: ReactNode }) {
  return <div><dt className="text-xs font-medium text-muted-foreground">{label}</dt><dd className="mt-1 text-sm font-medium text-foreground">{value}</dd></div>;
}

export function PiiChips({ items }: { items: string[] }) {
  return <div className="flex flex-wrap gap-1.5">{items.map((item) => <span key={item} className="rounded bg-data px-2 py-1 text-xs font-medium text-data-foreground">{item}</span>)}</div>;
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return <div className="grid min-h-52 place-items-center border border-dashed border-border bg-background p-8 text-center"><div><div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-muted"><CircleOff className="text-muted-foreground" /></div><h3 className="font-semibold">{title}</h3><p className="mt-1 text-sm text-muted-foreground">{body}</p></div></div>;
}

const translationLocales = [
  { code: "HI", locale: "hi" },
  { code: "KN", locale: "kn" },
  { code: "TA", locale: "ta" },
  { code: "TE", locale: "te" },
] as const;

const translationCache = new Map<string, string>();

async function translateEnglish(text: string, locale: string) {
  const key = `${locale}\n${text}`;
  const cached = translationCache.get(key);
  if (cached) return cached;
  const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${locale}&dt=t&q=${encodeURIComponent(text)}`);
  if (!response.ok) throw new Error("Translation failed");
  const body = await response.json() as [Array<[string]>?];
  const translated = (body[0] ?? []).map((part) => part?.[0] ?? "").join("").trim();
  if (!translated) throw new Error("Translation failed");
  translationCache.set(key, translated);
  return translated;
}

function useEnglishDraft(sources: { key: string; text: string }[], read: (field: string) => string, write: (field: string, value: string) => void) {
  const drafts = useRef<Record<string, string>>({});
  const edited = useRef<Set<string>>(new Set());
  const readRef = useRef(read);
  const writeRef = useRef(write);
  readRef.current = read;
  writeRef.current = write;
  const [status, setStatus] = useState<"idle" | "translating" | "error">("idle");
  const wave = useRef(0);
  const sourceText = sources.map((source) => source.text).join("\n");

  async function translate() {
    const jobs = sources.flatMap((source) => {
      const text = source.text.trim();
      if (text.length < 2) return [];
      return translationLocales.map((language) => ({ field: `${language.code}:${source.key}`, text, locale: language.locale }));
    });
    if (!jobs.length) return;
    const current = wave.current + 1;
    wave.current = current;
    setStatus("translating");
    let wrote = 0;
    for (const job of jobs) {
      if (wave.current !== current) return;
      try {
        const translated = await translateEnglish(job.text, job.locale);
        if (wave.current !== current) return;
        const existing = readRef.current(job.field);
        const previous = drafts.current[job.field];
        if (edited.current.has(job.field) && existing.trim() && existing !== previous) continue;
        drafts.current[job.field] = translated;
        wrote += 1;
        if (existing !== translated) writeRef.current(job.field, translated);
      } catch {
        if (wave.current !== current) return;
      }
    }
    if (wave.current === current) setStatus(wrote ? "idle" : "error");
  }

  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;
  const translateRef = useRef(translate);
  translateRef.current = translate;
  useEffect(() => {
    if (!sourcesRef.current.some((source) => source.text.trim().length >= 2)) return;
    const timer = window.setTimeout(() => { void translateRef.current(); }, 700);
    return () => window.clearTimeout(timer);
  }, [sourceText]);

  return {
    status,
    translate,
    noteEdit(field: string, value: string) {
      if (value === drafts.current[field]) edited.current.delete(field);
      else edited.current.add(field);
    },
  };
}

function TranslateBar({ status, disabled, onTranslate }: { status: "idle" | "translating" | "error"; disabled: boolean; onTranslate: () => void }) {
  return <div className="space-y-1">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs text-muted-foreground">Translate fills these from the English text. Correct any line before you save.</p>
      <Button type="button" variant="outline" size="sm" disabled={disabled || status === "translating"} onClick={onTranslate}>Translate</Button>
    </div>
    {status === "translating" && <p className="text-xs text-muted-foreground">Translating...</p>}
    {status === "error" && <p className="text-xs text-warning-foreground">Could not translate. Type the other languages yourself.</p>}
  </div>;
}

export function LanguageNameFields({ english, values, onChange }: { english: string; values: Record<string, string>; onChange: (code: string, value: string) => void }) {
  const draft = useEnglishDraft([{ key: "name", text: english }], (field) => values[field.slice(0, 2)] ?? "", (field, value) => onChange(field.slice(0, 2), value));
  return <div className="space-y-3">
    <TranslateBar status={draft.status} disabled={english.trim().length < 2} onTranslate={() => { void draft.translate(); }} />
    <div className="grid gap-3 sm:grid-cols-2">{catalogLanguages.filter((language) => language.code !== "EN").map((language) => <div key={language.code}><label className="text-xs font-semibold" htmlFor={`name-${language.code}`}>{language.label} name</label><Input id={`name-${language.code}`} className="mt-2" value={values[language.code] ?? ""} onChange={(event) => { draft.noteEdit(`${language.code}:name`, event.target.value); onChange(language.code, event.target.value); }} /></div>)}</div>
  </div>;
}

export function NoticeTranslationFields({ englishTitle, englishBody, values, onChange }: { englishTitle: string; englishBody: string; values: Record<string, { title: string; body: string }>; onChange: (code: string, field: "title" | "body", value: string) => void }) {
  const draft = useEnglishDraft(
    [{ key: "title", text: englishTitle }, { key: "body", text: englishBody }],
    (field) => { const [code, part] = field.split(":"); return values[code]?.[part as "title" | "body"] ?? ""; },
    (field, value) => { const [code, part] = field.split(":"); onChange(code, part as "title" | "body", value); },
  );
  return <div className="space-y-3">
    <TranslateBar status={draft.status} disabled={englishTitle.trim().length < 2 && englishBody.trim().length < 2} onTranslate={() => { void draft.translate(); }} />
    <div className="space-y-4">{catalogLanguages.filter((language) => language.code !== "EN").map((language) => <div key={language.code} className="space-y-2 border border-border p-3"><p className="text-xs font-semibold">{language.label}</p><Input aria-label={`${language.label} title`} value={values[language.code]?.title ?? ""} onChange={(event) => { draft.noteEdit(`${language.code}:title`, event.target.value); onChange(language.code, "title", event.target.value); }} placeholder="Title" /><textarea aria-label={`${language.label} notice text`} className="w-full px-3 py-2" rows={4} value={values[language.code]?.body ?? ""} onChange={(event) => { draft.noteEdit(`${language.code}:body`, event.target.value); onChange(language.code, "body", event.target.value); }} placeholder="Notice text" /></div>)}</div>
  </div>;
}

export function NoticeDetailsDialog({ open, onOpenChange, notice, version, purposes, language, onAgree, onCancel, agreeLabel = "Agree", cancelLabel = "Cancel" }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notice?: Pick<NoticeGroup, "id" | "name" | "description" | "owner">;
  version?: NoticeVersion;
  purposes: { id: string; name: string; principal: string; pii: string[]; basis: string; recipients: string; retention: string; pwd: string }[];
  language: string;
  onAgree?: () => void;
  onCancel?: () => void;
  agreeLabel?: string;
  cancelLabel?: string;
}) {
  const selectedCode = languageCode(language);
  const noticeText = version?.texts?.find((text) => text.language === selectedCode && text.body.trim()) ?? version?.texts?.find((text) => text.language === "EN" && text.body.trim());
  const noticeBody = noticeText?.body && noticeText.body !== notice?.description ? noticeText.body : "";
  const decisionRequired = !!onAgree;
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{notice?.name}</DialogTitle>
        <DialogDescription>{notice?.id} · {version?.version}</DialogDescription>
      </DialogHeader>
      {notice && version && <div className="space-y-5">
        <div className="grid grid-cols-2 gap-5">
          <Field label="Status" value={version.status || "—"} />
          <Field label="Effective on" value={version.date ? formatRetention(version.date) : "—"} />
          <Field label="Owner" value={notice.owner || "—"} />
          <Field label="Language" value={languageLabel(noticeText?.language || selectedCode)} />
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold">What this notice covers</h3>
          <p className="text-sm leading-6 text-muted-foreground">{notice.description || "No description is stored for this notice."}</p>
          {noticeBody && <p className="mt-2 text-sm leading-6 text-muted-foreground">{noticeBody}</p>}
        </div>
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Purposes on this notice</h3>
          {purposes.length === 0 && <p className="text-sm text-muted-foreground">No purposes are linked to this notice version.</p>}
          {purposes.map((purpose) => <div key={purpose.id} className="space-y-3 border border-border p-4">
            <div>
              <p className="text-sm font-semibold">{purpose.name}</p>
              <p className="text-xs text-muted-foreground">{purpose.id}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Data Principal category" value={purpose.principal || "—"} />
              <Field label="Lawful basis" value={purpose.basis || "—"} />
              <Field label="Retention" value={formatPurposeRetention(purpose.retention)} />
              <Field label="Involves PwD data" value={purpose.pwd || "—"} />
            </div>
            <div>
              <h4 className="mb-2 text-xs font-semibold">Data categories</h4>
              {purpose.pii.length ? <PiiChips items={purpose.pii} /> : <p className="text-sm text-muted-foreground">None</p>}
            </div>
            <div>
              <h4 className="mb-1 text-xs font-semibold">Providers</h4>
              <p className="text-sm text-muted-foreground">{purpose.recipients || "None"}</p>
            </div>
          </div>)}
        </div>
      </div>}
      {decisionRequired ? <DialogFooter className="mt-6 gap-2 border-t border-border pt-4 sm:space-x-0">
        <Button type="button" variant="outline" onClick={() => { onCancel?.(); onOpenChange(false); }}>{cancelLabel}</Button>
        <Button type="button" onClick={() => { onAgree?.(); onOpenChange(false); }}>{agreeLabel}</Button>
      </DialogFooter> : null}
    </DialogContent>
  </Dialog>;
}
